import { useEffect, useState, useContext, useCallback, useRef, useMemo } from "react"
import debounce from 'lodash.debounce';
import AppContext from "../services/context.js";
import { useLazyQuery, useQuery } from "@apollo/client";
import { SEARCH_DB, SEARCH_VEC, GET_CATEGORIES, GET_THERAPEUTIC_EFFECTS } from "../services/api.js";
import Select from "react-select";
import clsx from "clsx";
import Tooltip from "./Tooltip.jsx";
import fetchAiSearchResponse from "../services/aiSearch.js";
import PropTypes from "prop-types";
import { trackEvent } from "../lib/analytics.js";
import { useError } from "../hooks/useError.js";
import { useNavigate } from "react-router-dom";

const HelpIcon = () => (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fillRule="evenodd" clipRule="evenodd" d="M0 10C0 4.48001 4.47998 0 10 0C15.52 0 20 4.48001 20 10C20 15.52 15.52 20 10 20C4.47998 20 0 15.52 0 10ZM11 14V16H9V14H11ZM10 18C5.58997 18 2 14.41 2 10C2 5.59 5.58997 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM6 8C6 5.79001 7.79004 4 10 4C12.21 4 14 5.79001 14 8C14 9.28293 13.21 9.97333 12.4408 10.6455C11.7111 11.2833 11 11.9046 11 13H9C9 11.1787 9.94214 10.4566 10.7704 9.82169C11.4203 9.32361 12 8.87921 12 8C12 6.89999 11.1 6 10 6C8.90002 6 8 6.89999 8 8H6Z" fill="currentColor" />
</svg>
)

const searchHelpContent = (
  <div className="space-y-2">
    <p className="font-semibold">Como usar a busca:</p>
    <ul className="text-xs space-y-1">
      <li>• Nome de espécie</li>
      <li>• Doença ou sintoma</li>
      <li>• Efeitos terapêuticos</li>
      <li>• Metabólitos secundários</li>
    </ul>
    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
      Digite e aguarde os resultados aparecerem automaticamente ou clique no botão de busca.
    </p>
  </div>
);

const AI_PARTICLE_CONFIGS = [
  { top: '14%', left: '18%', driftX: '36px', driftY: '-52px', duration: '4.8s', delay: '-1.6s', startScale: '0.45', endScale: '0.18' },
  { top: '32%', left: '60%', driftX: '-22px', driftY: '-60px', duration: '5.6s', delay: '-0.4s', startScale: '0.5', endScale: '0.15', variant: 'sway' },
  { top: '58%', left: '28%', driftX: '18px', driftY: '-68px', duration: '6.4s', delay: '-2.4s', startScale: '0.4', endScale: '0.12', variant: 'arc' },
  { top: '72%', left: '64%', driftX: '-28px', driftY: '-56px', duration: '6s', delay: '-3.1s', startScale: '0.38', endScale: '0.1' },
  { top: '46%', left: '12%', driftX: '42px', driftY: '-62px', duration: '5.1s', delay: '-0.9s', startScale: '0.42', endScale: '0.18', variant: 'ember' },
  { top: '24%', left: '82%', driftX: '-40px', driftY: '-54px', duration: '5.8s', delay: '-1.8s', startScale: '0.4', endScale: '0.13' },
  { top: '68%', left: '10%', driftX: '30px', driftY: '-76px', duration: '5.7s', delay: '-2.9s', startScale: '0.45', endScale: '0.14', variant: 'arc' },
  { top: '88%', left: '40%', driftX: '6px', driftY: '-50px', duration: '6.8s', delay: '-2.5s', startScale: '0.3', endScale: '0.11', variant: 'sway' },
  { top: '8%', left: '52%', driftX: '-12px', driftY: '-46px', duration: '4.4s', delay: '-1.2s', startScale: '0.35', endScale: '0.1', variant: 'ember' },
  { top: '38%', left: '92%', driftX: '-48px', driftY: '-48px', duration: '6.2s', delay: '-3.5s', startScale: '0.44', endScale: '0.14', variant: 'arc' },
  { top: '56%', left: '50%', driftX: '16px', driftY: '-58px', duration: '5.5s', delay: '-0.7s', startScale: '0.36', endScale: '0.12' },
  { top: '30%', left: '6%', driftX: '38px', driftY: '-44px', duration: '4.9s', delay: '-0.5s', startScale: '0.4', endScale: '0.16', variant: 'sway' },
  { top: '76%', left: '78%', driftX: '-34px', driftY: '-66px', duration: '5.9s', delay: '-1.9s', startScale: '0.43', endScale: '0.13' },
  { top: '50%', left: '34%', driftX: '20px', driftY: '-60px', duration: '5.4s', delay: '-2.1s', startScale: '0.37', endScale: '0.12', variant: 'ember' },
  { top: '16%', left: '70%', driftX: '-24px', driftY: '-58px', duration: '5.2s', delay: '-2.7s', startScale: '0.4', endScale: '0.11' },
  { top: '82%', left: '24%', driftX: '28px', driftY: '-72px', duration: '6.3s', delay: '-3s', startScale: '0.41', endScale: '0.12', variant: 'arc' },
];

const AiParticleField = ({ active }) => {
  if (!active) {
    return null;
  }

  return (
    <div className="ai-mode-layer" aria-hidden="true">
      <span className="ai-mode-glow ai-mode-glow-primary" />
      <span className="ai-mode-glow ai-mode-glow-secondary" />
      <span className="ai-mode-glow ai-mode-glow-tertiary" />
      <span className="ai-mode-particles">
        {AI_PARTICLE_CONFIGS.map((particle, index) => (
          <span
            key={`ai-particle-${index}`}
            className={clsx(
              "ai-mode-particle",
              particle.variant && `ai-mode-particle--${particle.variant}`
            )}
            style={{
              top: particle.top,
              left: particle.left,
              animationDelay: particle.delay,
              animationDuration: particle.duration,
              '--particle-drift-x': particle.driftX,
              '--particle-drift-y': particle.driftY,
              '--particle-start-scale': particle.startScale,
              '--particle-end-scale': particle.endScale,
            }}
          />
        ))}
      </span>
    </div>
  );
};

AiParticleField.propTypes = {
  active: PropTypes.bool.isRequired,
};

const customStyles = (isDarkTheme) => ({
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderRadius: "0.375rem",
    fontSize: "0.8rem",
    minHeight: "34px",
    borderColor: state.isFocused
      ? (isDarkTheme ? "#71B257" : "#156D2D")
      : (isDarkTheme ? "#374151" : "#d1d5db"),
    backgroundColor: isDarkTheme ? "#1f2937" : "#ffffff",
    boxShadow: state.isFocused
      ? (isDarkTheme ? "0 0 0 2px rgba(113, 178, 87, 0.2)" : "0 0 0 2px rgba(21, 109, 45, 0.2)")
      : "none",
    "&:hover": {
      borderColor: isDarkTheme ? "#71B257" : "#156D2D",
    },
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: "0.875rem",
    backgroundColor: state.isSelected
      ? (isDarkTheme ? "#156D2D" : "#71B257")
      : state.isFocused
        ? (isDarkTheme ? "#374151" : "#f3f4f6")
        : (isDarkTheme ? "#1f2937" : "#ffffff"),
    color: state.isSelected
      ? "#ffffff"
      : (isDarkTheme ? "#f5f5f5" : "#111111"),
    cursor: "pointer",
    "&:active": {
      backgroundColor: isDarkTheme ? "#156D2D" : "#71B257",
    },
  }),
  multiValue: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "0.25rem",
    backgroundColor: isDarkTheme ? "#156D2D" : "#71B257",
    margin: "2px",
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    fontSize: "0.75rem",
    color: "#ffffff",
    fontWeight: "500",
    paddingLeft: "6px",
    paddingRight: "3px",
  }),
  multiValueRemove: (baseStyles) => ({
    ...baseStyles,
    color: "#ffffff",
    "&:hover": {
      backgroundColor: isDarkTheme ? "#0f5221" : "#5a8f44",
      color: "#ffffff",
    },
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "0.5rem",
    backgroundColor: isDarkTheme ? "#1f2937" : "#ffffff",
    border: isDarkTheme ? "1px solid #374151" : "1px solid #d1d5db",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  }),
  menuList: (baseStyles) => ({
    ...baseStyles,
    padding: "0.25rem",
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    color: isDarkTheme ? "#f5f5f5" : "#111111",
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    padding: "2px 6px",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    color: isDarkTheme ? "#9ca3af" : "#6b7280",
    fontSize: "0.8rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    color: isDarkTheme ? "#f5f5f5" : "#111111",
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    color: isDarkTheme ? "#71B257" : "#156D2D",
    "&:hover": {
      color: isDarkTheme ? "#8fc973" : "#0f5221",
    },
  }),
  clearIndicator: (baseStyles) => ({
    ...baseStyles,
    color: isDarkTheme ? "#71B257" : "#156D2D",
    "&:hover": {
      color: isDarkTheme ? "#8fc973" : "#0f5221",
    },
  }),
  indicatorSeparator: (baseStyles) => ({
    ...baseStyles,
    backgroundColor: isDarkTheme ? "#374151" : "#d1d5db",
  }),
});

const SearchInput = ({ mobileActionsEnd = null }) => {
  const { showError } = useError();
  const
    [rawFilters, setRawFilters] = useState({
      therapeuticEffects: [],
      categories: []
    }),
    [showLabel, setShowLabel] = useState(false),
    [showDisclaimer, setShowDisclaimer] = useState(false),
    { searchResults, setSearchResults, individualResult, setIndividualResult, setLoading, searchTerm, setSearchTerm, themeKey, isAiMode, setIsAiMode, setAiResponse, aiLoading, setAiLoading, setActiveFilters, userState, semanticSearchEnabled } = useContext(AppContext),
    [getSearchDb, { data: searchDbData, loading: loadingDb }] = useLazyQuery(SEARCH_DB, {
      onError: (error) => showError(error, { fallback: 'Falha ao buscar resultados.' }),
    }),
    [getSearchVec, { data: searchVecData, loading: loadingVec }] = useLazyQuery(SEARCH_VEC, {
      onError: (error) => showError(error, { fallback: 'Falha ao buscar resultados semânticos.' }),
    }),
    { data: categoriesData } = useQuery(GET_CATEGORIES, {
      onError: (error) => showError(error, { fallback: 'Falha ao carregar categorias.' }),
    }),
    { data: therapeuticEffectsData } = useQuery(GET_THERAPEUTIC_EFFECTS, {
      onError: (error) => showError(error, { fallback: 'Falha ao carregar efeitos terapêuticos.' }),
    }),
    inputRef = useRef(null),
    aiRequestControllerRef = useRef(null)
  const navigate = useNavigate();

  const isDarkTheme = themeKey === 'dark';
  const [aiBlockedReason, setAiBlockedReason] = useState('');
  const isPlusActive = Boolean(userState?.canUsePlus);
  const canUseSemanticSearch = semanticSearchEnabled && isPlusActive;
  const isPendingActivation = userState?.profile?.status === 'pending';
  const aiModePreferenceEnabled = userState?.profile?.preferences?.aiMode === true;
  const shouldInlineMobileHelp = !userState.isAuthenticated;
  const shouldShowMobileActions = !shouldInlineMobileHelp && (aiModePreferenceEnabled || mobileActionsEnd);
  const submittedFilters = useMemo(() => ({
    therapeuticEffects: rawFilters.therapeuticEffects.map((effect) => ({
      id: effect.value,
      label: effect.label,
    })),
    categories: rawFilters.categories.map((category) => ({
      id: category.value,
      label: category.label,
    })),
  }), [rawFilters]);
  const selectedFilterIds = useMemo(() => ({
    therapeuticEffects: rawFilters.therapeuticEffects.map(effect => effect.value) || [],
    categories: rawFilters.categories.map(category => category.value) || []
  }), [rawFilters]);

  const executeSearchQueries = useCallback((value) => {
    setSearchResults([])
    setIndividualResult({})
    setActiveFilters(submittedFilters)
    const variables = { q: value || null, filter: selectedFilterIds }
    getSearchDb({ variables })

    const filterCount = Object.values(selectedFilterIds).reduce((total, values) => total + values.length, 0)
    trackEvent('search', {
      search_mode: canUseSemanticSearch ? 'text_and_semantic' : 'text',
      has_query: Boolean((value || '').trim()),
      query_length: (value || '').trim().length,
      has_filters: filterCount > 0,
      filter_count: filterCount,
      plus_active: canUseSemanticSearch,
    })

    // Regra de negócio: busca semântica só é consultada quando a capability
    // Plus está ativa; visitantes e usuários sem acesso usam apenas a busca textual.
    if (canUseSemanticSearch) {
      getSearchVec({ variables: { q: value || null } })
    }
  }, [canUseSemanticSearch, getSearchDb, getSearchVec, selectedFilterIds, setActiveFilters, setIndividualResult, setSearchResults, submittedFilters])

  // Debounced version of handleSearch function with a delay of 500ms
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleSearch = useCallback(debounce((value) => {
    executeSearchQueries(value)
  }, 500), [executeSearchQueries]);

  useEffect(() => () => {
    debouncedHandleSearch.cancel();
  }, [debouncedHandleSearch]);

  const handleSearch = (e) => {
    const value = e?.target?.value || "";
    setSearchTerm(value);

    if (isAiMode) {
      if (aiRequestControllerRef.current) {
        aiRequestControllerRef.current.abort();
        aiRequestControllerRef.current = null;
      }
      setAiLoading(false);
      setAiResponse(null);
      return;
    }

    debouncedHandleSearch(value);
  };

  const handleShowDisclaimerTooltip = () => {
    localStorage.setItem('disclaimer-verified', 'true')
    setShowDisclaimer(false)
  }

  const handleFocus = () => {
    setShowLabel(false)
    if (localStorage.getItem('disclaimer-verified') !== 'true') setShowDisclaimer(true)
  }

  const handleBlur = () => {
    if (!searchTerm && Boolean(searchResults.length || individualResult.title)) { setShowLabel(true) }
  }

  const handleToggleAiMode = () => {
    if (!isPlusActive) {
      setAiBlockedReason('Habilite o plano Plus ativo para usar a busca com IA (RN-34 a RN-38).');
      return;
    }
    const nextMode = !isAiMode;
    setIsAiMode(nextMode);

    if (aiRequestControllerRef.current) {
      aiRequestControllerRef.current.abort();
      aiRequestControllerRef.current = null;
    }

    setAiLoading(false);
    setAiResponse(null);

    if (nextMode) {
      navigate('/');
      debouncedHandleSearch.cancel();
      setSearchResults([]);
      setIndividualResult({});
    } else if (searchTerm) {
      debouncedHandleSearch(searchTerm);
    }
  }

  const handleAiSubmit = async () => {
    if (!isPlusActive) {
      setAiBlockedReason('Plano Free não permite requisições de IA. Ative o Plus para continuar.');
      trackEvent('ai_search_blocked', {
        reason: 'plan_not_active',
        plan: userState?.currentPlan || 'FREE',
      })
      return;
    }
    const question = (searchTerm || '').trim();

    if (!question) {
      if (aiRequestControllerRef.current) {
        aiRequestControllerRef.current.abort();
        aiRequestControllerRef.current = null;
      }
      setAiLoading(false);
      setAiResponse(null);
      return;
    }

    if (aiRequestControllerRef.current) {
      aiRequestControllerRef.current.abort();
    }

    const controller = new AbortController();
    aiRequestControllerRef.current = controller;

    setAiLoading(true);
    setAiResponse(null);

    trackEvent('ai_search_submit', {
      flow_name: 'ai_search',
      plan: userState?.currentPlan || 'PLUS',
      query_length: question.length,
    })

    try {
      const response = await fetchAiSearchResponse(question, { signal: controller.signal });

      if (!controller.signal.aborted) {
        setAiResponse(response ?? { items: [] });
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        showError(error, { fallback: 'Não foi possível gerar uma resposta no momento. Tente novamente em alguns instantes.' });
        setAiResponse({
          error: 'Não foi possível gerar uma resposta no momento. Tente novamente em alguns instantes.',
          items: [],
        });
      }
    } finally {
      if (!controller.signal.aborted) {
        setAiLoading(false);
      }
      if (aiRequestControllerRef.current === controller) {
        aiRequestControllerRef.current = null;
      }
    }
  }

  const handleSubmit = () => {
    if (isAiMode) {
      handleAiSubmit();
      return;
    }

    debouncedHandleSearch.cancel();
    executeSearchQueries(searchTerm);
  }

  useEffect(() => {
    if (localStorage.getItem('disclaimer-verified') === 'true') {
      setShowDisclaimer(false)
    }

    return () => {
      debouncedHandleSearch.cancel();

      if (aiRequestControllerRef.current) {
        aiRequestControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchTerm && Boolean(searchResults.length || individualResult.title)) { setShowLabel(true) }
  }, [searchTerm, searchResults, individualResult])

  useEffect(() => {
    setLoading(loadingDb || loadingVec)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingDb, loadingVec])

  const mergeResults = useCallback((currentResults = [], incomingResults = []) => {
    const merged = new Map()

    currentResults.forEach((result) => {
      merged.set(result.q, result)
    })

    incomingResults.forEach((result) => {
      const existing = merged.get(result.q)
      if (!existing) {
        merged.set(result.q, result)
        return
      }
      // Enriquece com os campos semânticos quando o item já existia (vindo de searchDb).
      merged.set(result.q, {
        ...existing,
        score: existing.score ?? result.score,
        snippet: existing.snippet || result.snippet,
        sourceLabel: existing.sourceLabel || result.sourceLabel,
      })
    })

    return Array.from(merged.values())
  }, [])

  useEffect(() => {
    if (!isPlusActive && isAiMode) {
      setIsAiMode(false)
    }
    if (isPlusActive) {
      setAiBlockedReason('')
    }
  }, [isPlusActive, isAiMode, setIsAiMode])

  useEffect(() => {
    if (!aiModePreferenceEnabled && isAiMode) {
      setIsAiMode(false)
    }
  }, [aiModePreferenceEnabled, isAiMode, setIsAiMode])

  useEffect(() => {
    if (searchDbData?.searchDb) {
      setSearchResults((previousResults) => mergeResults(previousResults, searchDbData.searchDb))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDbData])

  useEffect(() => {
    if (searchVecData?.searchVec) {
      setSearchResults((previousResults) => mergeResults(previousResults, searchVecData.searchVec))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVecData])

  const showingResults = Boolean(searchResults.length || individualResult.title);
  const isSearchLoading = loadingDb || (canUseSemanticSearch && loadingVec);

  const aiToggleButtonClasses = clsx(
    "relative overflow-hidden px-3 py-1.5 text-xs font-semibold rounded-full transition-all duration-300 flex items-center gap-1.5 pointer-events-auto min-w-[4.75rem]",
    "border border-purple-200/60 bg-white text-purple-600 shadow-[0_12px_30px_rgba(192,132,252,0.35)]",
    "hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(192,132,252,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300",
    isAiMode && "ai-mode-button-active"
  );

  const actionButtonBaseClasses = clsx(
    "px-6 py-2 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2",
    "border border-purple-200/60 bg-white text-purple-700 shadow-[0_10px_30px_rgba(192,132,252,0.25)]",
    "hover:-translate-y-0.5 hover:shadow-[0_16px_45px_rgba(192,132,252,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300",
    isAiMode && "ai-mode-button-active"
  );

  const aiActionButtonClasses = clsx(
    actionButtonBaseClasses,
    aiLoading ? "opacity-70 cursor-wait" : "cursor-pointer"
  );

  const searchActionButtonClasses = clsx(
    actionButtonBaseClasses,
    isSearchLoading ? "opacity-70 cursor-wait" : "cursor-pointer"
  );

  const inputClasses = clsx(
    "relative z-10 border rounded-[20px_20px_20px_0] px-6 py-4 w-full transition-all duration-500 placeholder:text-gray-400 focus:outline-none active:outline-none",
    isAiMode
      ? [
        "pr-14 sm:pr-[8.5rem] border-purple-400/70 shadow-[0_0_35px_rgba(192,132,252,0.35)]",
        "hover:shadow-[0_0_45px_rgba(192,132,252,0.55)] focus:border-purple-500 focus:shadow-[0_0_60px_rgba(192,132,252,0.7)]",
        "active:border-purple-500 active:shadow-[0_0_45px_rgba(192,132,252,0.55)]",
        isDarkTheme ? "bg-slate-900/60 text-purple-100" : "bg-white text-purple-900",
      ]
      : [
        shouldInlineMobileHelp ? "pr-12 sm:pr-32" : "pr-4 sm:pr-32",
        "border-gray-600 shadow-[0_0_0_4px_rgba(0,0,0,0.25)]",
        "hover:shadow-[0_0_0_4px_rgba(0,0,0,0.5)] focus:border-green-600 focus:shadow-[0_0_0_4px_green]",
        "active:border-green-600 active:shadow-[0_0_0_4px_green]",
        isDarkTheme
          ? [
            "bg-greenbook-dark-bg text-greenbook-dark-text",
            "focus:border-greenbook-dark-primary active:border-greenbook-dark-primary",
          ]
          : [
            "bg-greenbook-light-bg text-greenbook-light-text",
            "focus:border-greenbook-light-primary active:border-greenbook-light-primary",
          ],
      ]
  );

  return (
    <div className="relative text-lg flex flex-col items-start w-full">
      <div className={clsx("w-full", isAiMode && "flex items-center gap-3 flex-nowrap")}>
        <div className={clsx("relative w-full", isAiMode && "flex-1 ai-mode-wrapper")}>
          <div className="absolute inset-y-0 right-3.5 z-20 hidden sm:flex items-center gap-1.5 pointer-events-none">
            <div className="pointer-events-auto">
              <Tooltip content={searchHelpContent} position="left">
                <div className={clsx(
                  "p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                  isDarkTheme ? "text-greenbook-dark-text" : "text-greenbook-light-text"
                )}>
                  <HelpIcon />
                </div>
              </Tooltip>
            </div>
            {aiModePreferenceEnabled && (
              <button
                type="button"
                onClick={handleToggleAiMode}
                className={aiToggleButtonClasses}
                aria-pressed={isAiMode}
              >
                <span>Modo IA</span>
              </button>
            )}
          </div>
          {shouldInlineMobileHelp && (
            <div className="absolute inset-y-0 right-3.5 z-20 flex sm:hidden items-center pointer-events-none">
              <div className="pointer-events-auto">
                <Tooltip content={searchHelpContent} position="left">
                  <div className={clsx(
                    "p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                    isDarkTheme ? "text-greenbook-dark-text" : "text-greenbook-light-text"
                  )}>
                    <HelpIcon />
                  </div>
                </Tooltip>
              </div>
            </div>
          )}
          {(aiBlockedReason || isPendingActivation) && (
            <div className={clsx(
              "mt-2 text-left text-xs rounded-lg px-3 py-2",
              isDarkTheme ? "bg-red-900/30 text-red-100" : "bg-red-50 text-red-700"
            )}>
              {isPendingActivation && <p>Sua conta está pendente de ativação. Valide um canal para liberar todos os recursos.</p>}
              {aiBlockedReason && <p className="mt-1">{aiBlockedReason}</p>}
            </div>
          )}
          {showLabel && (
            <label
              htmlFor="search"
              className="absolute top-3.5 left-6 text-gray-400"
            >
              Pesquisar no <strong className={clsx(
                "greenbook-logo",
                isDarkTheme ? "text-greenbook-dark-logo" : "text-greenbook-light-logo"
              )}>
                gr<span className={clsx(
                  "green",
                  isDarkTheme ? "text-greenbook-dark-logo-accent" : "text-greenbook-light-logo-accent"
                )}>ee</span>nbook
              </strong>
            </label>
          )}
          <input
            type="search"
            id="search"
            placeholder={showingResults ? "" : "digite para pesquisar um nome de planta, sintomas, metabólitos secundários..."}
            value={searchTerm}
            onChange={e => handleSearch(e)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={inputRef}
            className={clsx(inputClasses, isAiMode ? "mb-0" : "")}
          />
          <AiParticleField active={isAiMode} />
          {isAiMode && (
            <div className="absolute inset-y-0 right-2 z-20 flex sm:hidden items-center">
              <button
                type="button"
                onClick={handleSubmit}
                className={clsx(
                  "h-10 w-10 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0",
                  "border border-purple-200/60 bg-white text-purple-600 shadow-[0_4px_12px_rgba(192,132,252,0.35)]",
                  "hover:shadow-[0_6px_16px_rgba(192,132,252,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300",
                  aiLoading ? "opacity-70 cursor-wait" : "cursor-pointer",
                  "ai-mode-button-active"
                )}
                disabled={aiLoading}
                aria-busy={aiLoading}
                aria-label={aiLoading ? "Gerando resposta..." : "Enviar pergunta"}
              >
                {aiLoading ? (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="2" x2="12" y2="6" />
                    <line x1="12" y1="18" x2="12" y2="22" />
                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                    <line x1="2" y1="12" x2="6" y2="12" />
                    <line x1="18" y1="12" x2="22" y2="12" />
                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
        {isAiMode && (
          <div className="hidden sm:flex">
            <button
              type="button"
              onClick={handleSubmit}
              className={clsx(aiActionButtonClasses, "flex-shrink-0 px-5 relative z-20")}
              disabled={aiLoading}
              aria-busy={aiLoading}
            >
              {aiLoading && (
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              )}
              <span>{aiLoading ? "Gerando..." : "Perguntar"}</span>
            </button>
          </div>
        )}
      </div>
      {shouldShowMobileActions && (
        <div className="flex sm:hidden w-full items-center justify-end gap-2 mt-2">
          <div className="pointer-events-auto flex items-center">
            <Tooltip content={searchHelpContent} position="left">
              <div className={clsx(
                "p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                isDarkTheme ? "text-greenbook-dark-text" : "text-greenbook-light-text"
              )}>
                <HelpIcon />
              </div>
            </Tooltip>
          </div>
          {aiModePreferenceEnabled && (
            <button
              type="button"
              onClick={handleToggleAiMode}
              className={aiToggleButtonClasses}
              aria-pressed={isAiMode}
            >
              <span>Modo IA</span>
            </button>
          )}
          {mobileActionsEnd}
        </div>
      )}
      {showDisclaimer && (
        <>
          <div
            className="fixed inset-0 z-40 bg-white/20 backdrop-blur-sm dark:bg-black/20"
            aria-hidden="true"
          />
          <div className="absolute left-0 right-0 top-16 mx-4 z-50">
            <div className="max-w-2xl mx-auto bg-red-50 dark:bg-red-900 border-2 border-red-500 dark:border-red-400 rounded-lg p-4 shadow-xl">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-red-800 dark:text-red-200 font-bold text-lg mb-2">
                    Aviso Importante
                  </h3>
                  <p className="text-red-700 dark:text-red-300 text-sm mb-4">
                    Este sistema foi desenvolvido como ferramenta de estudo durante o curso de Pós-Graduação em Fitoterapia.
                    As informações contidas <strong>não constituem indicação médica</strong>.
                    O uso inadequado de fitoterápicos pode causar danos graves à saúde.
                  </p>
                  <p className="text-red-600 dark:text-red-400 text-xs mb-4">
                    Sempre consulte um profissional de saúde qualificado antes de utilizar qualquer planta medicinal.
                  </p>
                  <button
                    onClick={handleShowDisclaimerTooltip}
                    className="w-full sm:w-auto px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                  >
                    Li e estou ciente
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {!isAiMode && (
        <div
          className={clsx(
            "mt-2 sm:mt-4 border rounded-2xl px-2 py-2 sm:px-4 sm:py-3 sm:ml-0 sm:w-full",
            showingResults
              ? "-ml-[3.75rem] w-[calc(100vw-1rem)]"
              : "-ml-2 w-[calc(100vw-1rem)]",
            isDarkTheme
              ? "border-greenbook-dark-primary bg-greenbook-dark-green"
              : "border-greenbook-light-primary bg-greenbook-light-green"
          )}
        >
          {/* Mobile: 2 linhas (selects / ?+Buscar). Desktop: 1 linha via sm:contents */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="grid grid-cols-2 gap-2 sm:contents">
              <Select
                isMulti
                options={therapeuticEffectsData?.therapeuticEffects.map(effect => ({ value: effect.id, label: effect.term }))}
                value={rawFilters?.therapeuticEffects || []}
                onChange={(selectedOptions) => {
                  setRawFilters({ ...rawFilters, therapeuticEffects: selectedOptions || [] });
                }}
                placeholder="Efeitos terapêuticos"
                menuPortalTarget={document.querySelector('body')}
                styles={customStyles(isDarkTheme)}
                className="w-full sm:flex-1 sm:min-w-0"
              />

              <Select
                isMulti
                options={categoriesData?.categories.map(category => ({ value: category.id, label: category.name }))}
                value={rawFilters?.categories || []}
                onChange={(selectedOptions) => {
                  setRawFilters({ ...rawFilters, categories: selectedOptions || [] });
                }}
                placeholder="Categorias"
                menuPortalTarget={document.querySelector('body')}
                styles={customStyles(isDarkTheme)}
                className="w-full sm:flex-1 sm:min-w-0"
              />
            </div>

            <div className="flex items-center justify-between sm:contents">
              <Tooltip
                content={
                  <div className="space-y-2">
                    <p className="font-semibold text-sm">Fi<span className="opacity-50">l</span>t<span className="opacity-50">r</span>o complexo</p>
                    <p className="text-xs">
                      O grande benefício de utilizar plantas no tratamento é fazer uso de sua combinação de efeitos terapêuticos resultante da interação de seus diversos metabólitos secundários.
                    </p>
                    <p className="text-xs">
                      Use esses filtros para refinar sua busca e encontrar plantas que atuem em múltiplas condições.
                    </p>
                  </div>
                }
                position="bottom"
              >
                <div className={clsx(
                  "p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors flex-shrink-0",
                  isDarkTheme ? "text-greenbook-dark-text" : "text-greenbook-light-text"
                )}>
                  <HelpIcon />
                </div>
              </Tooltip>

              <button
                type="button"
                onClick={handleSubmit}
                className={clsx(searchActionButtonClasses, "flex-shrink-0")}
                disabled={isSearchLoading}
                aria-busy={isSearchLoading}
              >
                {isSearchLoading ? (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="2" x2="12" y2="6" />
                    <line x1="12" y1="18" x2="12" y2="22" />
                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                    <line x1="2" y1="12" x2="6" y2="12" />
                    <line x1="18" y1="12" x2="22" y2="12" />
                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                )}
                <span>{isSearchLoading ? "Buscando..." : "Buscar"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

SearchInput.propTypes = {
  mobileActionsEnd: PropTypes.node,
};

SearchInput.defaultProps = {
  mobileActionsEnd: null,
};

export default SearchInput
